import type { Cost, RegenerationRate, ResourceId, Rules } from "./types.ts";

/**
 * The page's cost check near the threshold (ADR-073's amendment of 2026-10-02, item 6): `vitals` reach the page up to
 * 3 ticks stale while only regeneration moves them, and an act is judged about a one-way latency after it is sent, a
 * window of 5 or 6 ticks in all, so the page withholds a press for cost only when it is clearly short, and leaves the
 * edge to the server.
 */

const PERCENT = 100;

/**
 * The regeneration steps the window between the page's last `vitals` and the server's judgement can hold: the largest
 * rate, 4 ticks a step, lands at most twice in 5 or 6 ticks.
 */
export const SLACK_STEPS = 2;

/**
 * The points one step of a rate adds to a pool.
 *
 * @param rate The rate.
 * @param maximum The pool's maximum, which a percent is taken of.
 * @returns The points, a percent rounded up.
 */
function stepOf(rate: RegenerationRate, maximum: number): number {
  return rate.unit === "points" ? rate.amount : Math.ceil((rate.amount * maximum) / PERCENT);
}

/**
 * The most a resource may regenerate between the page's last `vitals` and the server's judgement of an act sent now:
 * `SLACK_STEPS` of its larger step, in or out of combat, a percent taken of its current maximum.
 *
 * @param rules The rules.
 * @param resource The resource.
 * @param maximum Its current maximum, as the last `vitals` carried it.
 * @returns Whole points.
 * @example
 * regenerationSlack(loadRules(), "stamina", 100);
 * // 4
 */
export function regenerationSlack(rules: Rules, resource: ResourceId, maximum: number): number {
  const { inCombat, outOfCombat } = rules.resources[resource].regeneration;
  return SLACK_STEPS * Math.max(stepOf(inCombat, maximum), stepOf(outOfCombat, maximum));
}

/**
 * Whether resources are clearly short of costs: some cost exceeds its resource even with the regeneration the window
 * may bring (`regenerationSlack`).
 *
 * @param rules The rules.
 * @param pools Each resource as the last `vitals` carried it.
 * @param maximums Each resource's maximum as the last `vitals` carried it.
 * @param costs The costs.
 * @returns True when the page may withhold the press for cost.
 */
export function clearlyShort(rules: Rules, pools: Readonly<Record<ResourceId, number>>, maximums: Readonly<Record<ResourceId, number>>, costs: readonly Cost[]): boolean {
  for (const cost of costs) {
    if (pools[cost.resource] + regenerationSlack(rules, cost.resource, maximums[cost.resource]) < cost.amount) {
      return true;
    }
  }
  return false;
}
